import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheck, MdClose } from "react-icons/md";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import FieldLabel from "src/components/FieldLabel/FieldLabel";
import IconSelector from "src/components/IconSelector/IconSelector";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import Modal from "src/components/Modal/Modal";
import CustomSelect from "src/components/Select/CustomSelect";
import TextAreaInput from "src/components/TextAreaInput/TextAreaInput";
import TextInput from "src/components/TextInput/TextInput";
import { ICON_SIZES } from "src/helpers/constants";

interface ISourceAddContactDetailsModal {
  onClose: () => void;
  onConfirm: (data: any) => void;
  isLoading: boolean;
}

export enum ECONTACT_TYPE {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  LINK = "LINK",
  SMS = "SMS",
}

const SourceAddContactDetailsModal: React.FC<ISourceAddContactDetailsModal> = ({
  onClose,
  onConfirm,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [contactType, setContactType] = useState<ECONTACT_TYPE>(
    ECONTACT_TYPE.PHONE
  );
  const [contactName, setContactName] = useState<string>("");
  const [contactData, setContactData] = useState<string>("");
  const [contactIcon, setContactIcon] = useState<string>("");

  const contactDataFields = useMemo(() => {
    switch (contactType) {
      case ECONTACT_TYPE.EMAIL: {
        return {
          valuePlaceholder: "Digita la dirección de correo electrónico",
          fieldType: "email",
          valueLabel: "Correo electrónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Correo electrónico, enviar correo, etc.",
        };
      }
      case ECONTACT_TYPE.LINK: {
        return {
          valuePlaceholder:
            "Digita o pega la dirección web (puedes agregar el enlace para contactar al usuario por WhatsApp, enlace de Google Maps, etc.)",
          fieldType: "url",
          valueLabel: "Dirección web",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Página web, Google Maps, WhatsApp, etc.",
        };
      }
      case ECONTACT_TYPE.PHONE: {
        return {
          valuePlaceholder: "Digita el número de teléfono",
          fieldType: "phone",
          valueLabel: "Número telefónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Teléfono principal, móvil, oficina, etc.",
        };
      }
      case ECONTACT_TYPE.SMS: {
        return {
          valuePlaceholder: "Digita el número de teléfono",
          fieldType: "phone",
          valueLabel: "Número telefónico",
          shortNameLabel: "Nombre corto",
          shortNamePlaceholder: "Teléfono principal, móvil, oficina, etc.",
        };
      }

      default: {
        return null;
      }
    }
  }, [contactType]);

  const canSave = useMemo(() => {
    return !!contactType && !!contactName && !!contactData && !!contactIcon;
  }, [contactType, contactName, contactData, contactIcon]);

  return (
    <Modal onEscape={() => onClose()} className={clsx(["md:!w-[700px]"])}>
      <Modal.Header
        title="Agregar información de contacto"
        onClose={() => onClose()}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
          <InfoBlock
            displayMobileIcon
            message="Recuerda guardar los cambios después de agregar la información de contacto."
          />
          <div
            className={clsx([
              "grid",
              "grid-cols-1",
              "gap-4",
              {
                "md:grid-cols-3": contactType !== ECONTACT_TYPE.LINK,
                "md:grid-cols-2": contactType === ECONTACT_TYPE.LINK,
              },
            ])}
          >
            <CustomSelect
              onChange={(e: any) => setContactType(e?.value)}
              isClearable={false}
              isSearchable={false}
              useValueAsLabel={false}
              required
              options={[
                {
                  value: ECONTACT_TYPE.LINK,
                  label: "Dirección web (URL)",
                },
                {
                  value: ECONTACT_TYPE.EMAIL,
                  label: "Correo electrónico",
                },
                {
                  value: ECONTACT_TYPE.PHONE,
                  label: "Teléfono",
                },
                {
                  value: ECONTACT_TYPE.SMS,
                  label: "Enviar SMS",
                },
              ]}
              value={contactType}
              label="Tipo"
              placeholder="Selecciona el tipo de contacto"
            />
            <TextInput
              value={contactName}
              onChange={(e) => setContactName(e?.target?.value)}
              placeholder={contactDataFields?.shortNamePlaceholder}
              label={contactDataFields?.shortNameLabel}
              required
              id="contact-name-field"
            />
            {contactType !== ECONTACT_TYPE.LINK && (
              <TextInput
                value={contactData}
                onChange={(e) => setContactData(e?.target?.value)}
                placeholder={contactDataFields?.valuePlaceholder}
                label={contactDataFields?.valueLabel}
                required
              />
            )}
          </div>
          {contactType === ECONTACT_TYPE.LINK && (
            <TextAreaInput
              value={contactData}
              onChange={(e) => setContactData(e?.target?.value)}
              placeholder={contactDataFields?.valuePlaceholder}
              label={contactDataFields?.valueLabel}
              required
            />
          )}
          <IconSelector
            icons={[
              "MdOutlinePlace",
              "MdOutlineMap",
              "MdOutlineGpsFixed",
              "MdOutlinePhone",
              "MdOutlinePhoneAndroid",
              "MdOutlineEmail",
              "MdOutlineAlternateEmail",
              "MdOutlineChat",
              "MdWhatsapp",
              "MdOutlineHome",
              "MdOutlineApartment",
              "MdOutlineFace",
              "MdOutlineFace2",
              "MdOutlineFace4",
              "MdOutlineFace5",
              "MdOutlineFace6",
              "MdOutlineSupportAgent",
              "MdOutlinePersonPinCircle",
              "MdOutlinePublic",
              "MdOutlineLink",
            ]}
            onClick={(e: any) => setContactIcon(e)}
            selected={contactIcon}
          />
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("Agregar"),
            onClick: () =>
              onConfirm({
                type: contactType,
                value: contactData,
                name: contactName,
                icon: contactIcon,
              }),
            kind: EButtonKind.SAVE,
            icon: <MdCheck size={ICON_SIZES.MD} />,
            disabled: !canSave || isLoading,
            isLoading,
          },
          {
            text: t("common_buttons.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
            disabled: isLoading,
          },
        ]}
      />
    </Modal>
  );
};

export default SourceAddContactDetailsModal;
